import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, List, ListItem, ListItemText, IconButton } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import HighlightedText from './HighlightedText';

/**
 * Component for displaying a list of keywords with search highlighting
 *
 * @param {Object} props Component props
 * @param {Array} props.keywords Array of keyword objects
 * @param {string} props.searchQuery Current search query for highlighting
 * @param {Function} props.onRemoveKeyword Function to handle keyword removal
 * @param {boolean} props.isRemoving Whether a keyword removal is in progress
 * @returns {JSX.Element} Rendered keywords list component
 */
const KeywordsList = ({ keywords, searchQuery, onRemoveKeyword, isRemoving = false }) => {
  return (
    <>
      {/* Keywords List Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          BLACKLISTED KEYWORDS
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ACTIONS
        </Typography>
      </Box>

      {/* Keywords List */}
      <List sx={{ py: 0 }}>
        {keywords.length > 0 ? (
          keywords.map((keyword) => (
            <ListItem
              key={keyword.id}
              sx={{
                px: 2,
                py: 1,
                mb: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => onRemoveKeyword(keyword.keyword)}
                  disabled={isRemoving}
                  sx={{ color: 'error.main' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText primary={<HighlightedText text={keyword.keyword} highlight={searchQuery} />} />
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText primary="No keywords found" sx={{ color: 'text.secondary', textAlign: 'center' }} />
          </ListItem>
        )}
      </List>
    </>
  );
};

KeywordsList.propTypes = {
  keywords: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      keyword: PropTypes.string.isRequired,
    })
  ).isRequired,
  searchQuery: PropTypes.string.isRequired,
  onRemoveKeyword: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
};

export default KeywordsList;
